
import { QueryClient } from '@tanstack/react-query';
import { shouldRefreshContentFeed } from '@/services/contentCache';

// Default stale time for queries
const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const DEFAULT_CACHE_TIME = 10 * 60 * 1000; // 10 minutes

// Content feed and PIF insights stale time (24 hours)
const CONTENT_STALE_TIME = 24 * 60 * 60 * 1000;

// Configure with optimized defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_STALE_TIME,
      gcTime: DEFAULT_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      retry: (failureCount, error) => {
        // Only retry network errors, not 404s or other server errors
        if (error instanceof Error && error.message.includes('Network Error')) {
          return failureCount < 3;
        }
        return false;
      },
    },
    mutations: {
      retry: 1,
      networkMode: 'always',
    },
  },
});

// Set up global error handling
queryClient.getQueryCache().subscribe(() => {
  // Using a more general subscription without specifying 'error' property
  // This avoids TypeScript errors while still allowing us to handle errors
  // through the QueryClient's default error handling
  console.log('Query cache updated');
});

// Configure special rules for content feed and PIF insights queries
// This will ensure they use the date-based caching mechanism
const configureQueryDefaults = () => {
  // Content feed queries - cache for a full day
  queryClient.setQueryDefaults(['content-feed'], {
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_STALE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
  
  // Mobile content feed queries - also cache for a full day
  queryClient.setQueryDefaults(['mobile-content-feed'], {
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_STALE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};

// Call this configuration function
configureQueryDefaults();

// For logging in development
if (process.env.NODE_ENV === 'development') {
  queryClient.setDefaultOptions({
    queries: {
      retry: false, // Don't retry in development
    },
  });
}

// Utility to prefetch common page data
export const prefetchPageData = async (page: string, params?: Record<string, any>) => {
  // Content Detail prefetching
  if (page === 'content-detail' && params?.id) {
    await queryClient.prefetchQuery({
      queryKey: ['content-post', params.id],
      queryFn: async () => {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data } = await supabase
          .from('social_posts')
          .select(`*, user_id, likes_count`)
          .eq('id', params.id)
          .single();
        return data;
      },
    });
  }
};

// Hook for optimistic UI updates with React Query
export const getOptimisticOptions = <T,>(
  queryKey: unknown[],
  updateFn: (oldData: T | undefined) => T
) => {
  return {
    onMutate: async (variables: unknown) => {
      // Cancel outgoing refetches that might overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey });
      
      // Get the current query data
      const previousData = queryClient.getQueryData<T>(queryKey);
      
      // Optimistically update the cache with our new value
      if (previousData !== undefined) {
        queryClient.setQueryData<T>(queryKey, updateFn(previousData));
      }
      
      // Return the previous data so we can revert if something goes wrong
      return { previousData };
    },
    onError: (err: Error, variables: unknown, context: any) => {
      // If the mutation fails, revert back to the previous value
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to make sure our optimistic update
      // is revalidated against the server
      queryClient.invalidateQueries({ queryKey });
    },
  };
};
